import { SPEEDS, type SpeedSetting } from '../ui/speed';

/**
 * Keyboard bindings, as named actions. A packaged build or a controller binds
 * the same actions to its own input.
 *
 * Space toggles pause; the digit keys 0 to 8 pick a rung of the speed ladder,
 * 0 being pause, in the order the buttons are drawn; Escape steps the game
 * menu back or closes it (M63). While the menu is open the clock's keys do
 * nothing: the menu holds the clock, and a key must not let it go behind it.
 */
export interface InputActions {
  togglePause(): void;
  setSpeed(speed: SpeedSetting): void;
  escape(): void;
  /** Whether the game menu is open. */
  menuOpen(): boolean;
}

/** What a key does (above): a function of the key and the menu alone, so the suite holds it without a browser. */
export type KeyAction = { kind: 'pause' } | { kind: 'speed'; speed: SpeedSetting } | { kind: 'escape' } | null;

export function keyAction(code: string, menuOpen: boolean): KeyAction {
  if (code === 'Escape') return { kind: 'escape' };
  if (menuOpen) return null;
  if (code === 'Space') return { kind: 'pause' };
  const digit = /^Digit(\d)$/.exec(code)?.[1];
  const speed = digit === undefined ? undefined : SPEEDS[Number(digit)];
  return speed === undefined ? null : { kind: 'speed', speed };
}

/**
 * Whether a key belongs to the control that has the keyboard, not to the game: every key in a text field, and every key
 * but Escape in a dropdown (review M190: a digit in a Management dropdown both chose a rule and changed the speed, Space
 * in it unpaused; Escape, which a closed dropdown does nothing with, still opens the menu). A focused button keeps the
 * game's keys: a clicked button holds the focus, and Space pausing there, as it always has, keeps it from pressing that
 * button again (review M190's second reviewer: Space on a clicked Load reloaded the quick slot).
 */
export function keyForControl(target: { tagName?: string } | null, code: string): boolean {
  const tag = target?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  return tag === 'SELECT' && code !== 'Escape';
}

export function bindKeyboard(actions: InputActions): void {
  window.addEventListener('keydown', (event) => {
    if (keyForControl(event.target as { tagName?: string } | null, event.code)) return;
    const action = keyAction(event.code, actions.menuOpen());
    if (action === null) return;
    if (action.kind === 'pause') {
      event.preventDefault();
      actions.togglePause();
    } else if (action.kind === 'speed') {
      actions.setSpeed(action.speed);
    } else {
      actions.escape();
    }
  });
}
